import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import {
  ChecklistData,
  GuidelineItem,
  MedicalNecessityReviewState,
} from "../../data-model/medicalNecessityReview";

const initialState: MedicalNecessityReviewState = {
  checklistData: null,
  userAnswers: {},
  emrDocumentUrl: null,
  emrDocumentUrls: [],
  currentDocumentIndex: 0,
  guidelineDocumentUrl: null,
  guidelineUrls: [],
  guidelineItems: [],
  loading: false,
  error: null,
  saving: false,
};

const medicalNecessityReviewSlice = createSlice({
  name: "medicalNecessityReview",
  initialState,
  reducers: {
    setChecklistData: (state, action: PayloadAction<ChecklistData>) => {
      state.checklistData = action.payload;
      state.error = null;
    },

    updateAnswer: (
      state,
      action: PayloadAction<{ questionId: string; answer: any }>,
    ) => {
      state.userAnswers[action.payload.questionId] = action.payload.answer;
    },

    setUserAnswers: (state, action: PayloadAction<Record<string, any>>) => {
      state.userAnswers = action.payload;
    },

    setEmrDocumentUrl: (state, action: PayloadAction<string>) => {
      state.emrDocumentUrl = action.payload;
      state.error = null;
    },

    setEmrDocumentUrls: (state, action: PayloadAction<string[]>) => {
      state.emrDocumentUrls = action.payload;
      state.currentDocumentIndex = 0; // Reset to first document
      state.error = null;
    },

    setCurrentDocumentIndex: (state, action: PayloadAction<number>) => {
      const maxIndex = state.emrDocumentUrls.length - 1;
      if (action.payload >= 0 && action.payload <= maxIndex) {
        state.currentDocumentIndex = action.payload;
      }
    },

    setGuidelineDocumentUrl: (state, action: PayloadAction<string>) => {
      state.guidelineDocumentUrl = action.payload;
    },

    setGuidelineUrls: (state, action: PayloadAction<string[]>) => {
      state.guidelineUrls = action.payload;
      state.error = null;
    },

    setGuidelineItems: (state, action: PayloadAction<GuidelineItem[]>) => {
      state.guidelineItems = action.payload;
      state.error = null;
    },

    setLoading: (state, action: PayloadAction<boolean>) => {
      state.loading = action.payload;
      if (action.payload) {
        state.error = null;
      }
    },

    setSaving: (state, action: PayloadAction<boolean>) => {
      state.saving = action.payload;
    },

    setError: (state, action: PayloadAction<string>) => {
      state.error = action.payload;
      state.loading = false;
      state.saving = false;
    },

    resetState: () => initialState,
  },
});

export const {
  setChecklistData,
  updateAnswer,
  setUserAnswers,
  setEmrDocumentUrl,
  setEmrDocumentUrls,
  setCurrentDocumentIndex,
  setGuidelineDocumentUrl,
  setGuidelineUrls,
  setGuidelineItems,
  setLoading,
  setSaving,
  setError,
  resetState,
} = medicalNecessityReviewSlice.actions;

export default medicalNecessityReviewSlice.reducer;
